import { useBrandSpecialQualifyingStatus } from '@iheartjane/data-access/react-query';
import {
  Box,
  ChevronRightIcon,
  Flex,
  JaneGoldIcon,
  Typography,
} from '@iheartjane/reefer';
import { useEffect, useState } from 'react';

import { LoadingWrapper } from '@jane/shared/components';
import type { Cart } from '@jane/shared/types';
import { brandSpecialOrProductHasSegments } from '@jane/shared/util';

import { useKioskSpacing } from '../../../../hooks';
import {
  BundleStatusesProvider,
  useBundleStatuses,
} from '../../../../providers';
import { CompleteBundleModal } from '../../CompleteBundleModal/CompleteBundleModal';
import type {
  BrandSpecial,
  JaneGoldBundleProgressCalloutContainerProps,
  JaneGoldBundleProgressCalloutProps,
} from '../JaneGoldCallout.types';

//This is a dummy component used to track the status of each bundle in progress
const BundleStatus = ({
  bundle,
  cart,
}: {
  bundle: BrandSpecial;
  cart?: Cart;
}) => {
  const { setBundleStatuses } = useBundleStatuses();
  const {
    data: bundleStatus,
    isLoading,
    refetch: refetchStatus,
    isRefetching,
  } = useBrandSpecialQualifyingStatus(
    {
      cartUuid: cart?.uuid,
      specialId: bundle.id,
      storeId: cart?.store?.id,
    },
    { enabled: !!cart }
  );

  useEffect(() => {
    if (cart?.uuid) {
      refetchStatus();
    }
  }, [cart?.uuid, cart?.products, refetchStatus]);

  useEffect(() => {
    if (isLoading || isRefetching) {
      setBundleStatuses((prev) => ({
        ...prev,
        [bundle.id]: 'loading',
      }));
    } else if (bundleStatus) {
      setBundleStatuses((prev) => ({
        ...prev,
        [bundle.id]: bundleStatus.qualifying_status,
      }));
    }
  }, [bundleStatus, bundle, isLoading, isRefetching]);

  return null;
};

export const JaneGoldBundleProgressCallout = ({
  bundlesInProgress,
  onClick,
  userSignedIn = false,
}: JaneGoldBundleProgressCalloutProps) => {
  const { getKioskSpacing } = useKioskSpacing();
  const { bundleStatuses } = useBundleStatuses();
  const [openModal, setOpenModal] = useState(false);

  const visibleBundlesInProgress = bundlesInProgress.filter((bundle) => {
    const status = bundleStatuses[bundle.id];
    return status !== 'applied';
  });

  const isLoading = visibleBundlesInProgress.some((bundle) => {
    const status = bundleStatuses[bundle.id];
    return status === 'loading' || !status;
  });

  if (!isLoading && visibleBundlesInProgress.length === 0) {
    return null;
  }

  const singleBundle = visibleBundlesInProgress.length === 1;

  const segmentedDeal = singleBundle
    ? brandSpecialOrProductHasSegments({
        brandSpecial: visibleBundlesInProgress[0],
      }) && !userSignedIn
    : false;

  return (
    <LoadingWrapper
      variant="block"
      height="80px"
      isLoading={isLoading}
      data-testid="loading"
      pt={getKioskSpacing(24, 16)}
    >
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        p={getKioskSpacing(24, 16)}
        borderRadius="sm"
        background="gold-light"
        style={{ border: '1px solid rgba(255, 194, 32, 0.40)' }}
        onClick={
          singleBundle
            ? () => onClick?.(visibleBundlesInProgress[0])
            : () => setOpenModal(true)
        }
        ariaLabel={`click to complete your bundle${singleBundle ? '' : 's'}`}
      >
        <Box>
          <JaneGoldIcon size="lg" mr={16} />
        </Box>
        <Flex flexDirection="column" mr={32}>
          <Typography branded variant="body-bold">
            You're almost there - complete your{' '}
            {singleBundle ? 'bundle' : 'bundles to get these deals'}!
          </Typography>
          {singleBundle && (
            <Typography branded>
              {visibleBundlesInProgress[0]?.product_brand?.name} •{' '}
              {visibleBundlesInProgress[0].title}
            </Typography>
          )}
          {segmentedDeal && (
            <Typography branded color="grays-mid">
              (Sign in for eligibility)
            </Typography>
          )}
        </Flex>
        <Box ml="auto">
          <ChevronRightIcon />
        </Box>
      </Flex>

      {!singleBundle && (
        <CompleteBundleModal
          bundlesInProgress={visibleBundlesInProgress}
          open={openModal}
          onRequestClose={() => setOpenModal(false)}
          onClick={onClick}
          userSignedIn={userSignedIn}
        />
      )}
    </LoadingWrapper>
  );
};

export const JaneGoldBundleProgressCalloutContainer = ({
  bundlesInProgress,
  onClick,
  cart,
  userSignedIn = false,
}: JaneGoldBundleProgressCalloutContainerProps) => {
  return (
    <BundleStatusesProvider>
      {bundlesInProgress.map((bundle) => (
        <BundleStatus key={bundle.id} bundle={bundle} cart={cart} />
      ))}
      <JaneGoldBundleProgressCallout
        bundlesInProgress={bundlesInProgress}
        onClick={onClick}
        userSignedIn={userSignedIn}
      />
    </BundleStatusesProvider>
  );
};
